import { z } from "zod";
import type { QueryParams } from "../../httpClient";
import { AgencyTypeFieldsSchema, AgencyTypeSchema } from "../shared";

// Review Request Schemas
export const ReviewRequestSchema = z.object({
    id: z.number(),
    client_email: z.string().optional(),
    client_name: z.string().optional(),
    client_company: z.string().optional(),
    expert_name: z.string().optional(),
    expert_slug: z.string().optional(),
    expert_type: z.enum(["freelancer", "agency"]).optional(),
    agency_type: AgencyTypeSchema.optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
    project_id: z.number().nullable(),
    v2_project_id: z.number().nullable().optional(),
    project_name: z.string().nullable(),
    message: z.string().nullable(),
    status: z.enum(["pending", "completed", "expired", "cancelled"]),
    sent_at: z.string(), // ISO8601 datetime string
    token_expires_at: z.string(), // ISO8601 datetime string
    completed_at: z.string().nullable().optional(),
    reminders: z.object({
        auto_reminder_sent_at: z.string().nullable(),
        manual_reminder_sent_at: z.string().nullable(),
        manual_reminder_available: z.boolean(),
    }).optional(),
    review: z.object({
        id: z.number(),
        overall_rating: z.number(),
        status: z.string(),
    }).nullable().optional(),
});

// Client Review Request Schema (slightly different structure)
export const ClientReviewRequestSchema = z.object({
    id: z.number(),
    expert_name: z.string(),
    expert_slug: z.string(),
    expert_type: z.enum(["freelancer", "agency"]),
    agency_type: AgencyTypeSchema.optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
    expert_profile_picture: z.string().nullable(),
    project_id: z.number().nullable(),
    v2_project_id: z.number().nullable().optional(),
    project_name: z.string().nullable(),
    message: z.string().nullable(),
    status: z.enum(["pending", "completed", "expired"]),
    sent_at: z.string(), // ISO8601 datetime string
    token_expires_at: z.string(), // ISO8601 datetime string
    review_url: z.string().nullable(),
    review: z.object({
        id: z.number(),
        overall_rating: z.number(),
        status: z.string(),
    }).nullable(),
});

export const ReviewRequestCreationResponseSchema = z.object({
    review_request_id: z.number(),
    status: z.string(),
    expires_at: z.string(), // ISO8601 datetime string
    client: z.object({
        id: z.number(),
        email: z.string(),
        name: z.string(),
        company_name: z.string(),
    }),
});

// Query Parameter Types
export interface UserSearchQueryParams extends QueryParams {
    query: string; // min 2 characters
}

export interface ExpertReviewRequestsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    status?: "pending" | "completed" | "expired" | "cancelled";
}

export interface ClientReviewRequestsQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
    status?: "pending" | "completed" | "expired";
}

// Request Body Types
export interface CreateReviewRequestForExistingClientBody {
    client_id: number;
    project_id?: number;
    v2_project_id?: number;
    project_value_range_id?: number;
    message?: string;
}

export interface CreateReviewRequestForNewClientBody {
    client_email: string;
    client_first_name: string;
    client_last_name: string;
    client_company_name: string;
    client_company_website?: string;
    project_value_range_id?: number;
    message?: string;
}

export interface SendReminderBody {
    message?: string;
}

